import React, { useEffect } from 'react';
import { Title } from 'react-admin';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Breadcrumbs, Link, Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { getManualEntryBySlug } from '../index';
import MarkdownRenderer from './MarkdownRenderer';
import { track, EVENTS } from '../../lib/analytics';

const ManualPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const entry = getManualEntryBySlug(slug || '');

  useEffect(() => {
    if (entry) {
      track(EVENTS.MANUAL_PAGE_VIEWED, { slug: entry.slug, category: entry.category });
    }
  }, [entry?.slug]);

  // Link direto pra seção (?manual=x&section=y do Home.jsx, ou navegação com #hash): rola até o
  // heading depois que o MarkdownRenderer monta os ids (headingSlug). Recalcula no troca de hash.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const frame = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [location.hash, entry?.slug]);

  if (!entry) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h5">Página não encontrada</Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/manual')}
            sx={{ mt: 2 }}
          >
            Voltar ao Manual
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <Title title={`Manual - ${entry.title}`} />
      <CardContent>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/manual')}
            sx={{ cursor: 'pointer' }}
          >
            Manual
          </Link>
          <Typography color="text.primary">{entry.title}</Typography>
        </Breadcrumbs>

        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/manual')}
          sx={{ mb: 3 }}
          variant="outlined"
          size="small"
        >
          Voltar ao Manual
        </Button>

        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <MenuBookIcon sx={{ fontSize: 32, color: '#7869DF' }} />
          <Typography variant="h4" fontWeight={700}>{entry.title}</Typography>
        </Box>
        <Chip label={entry.category} size="small" sx={{ mb: 3, bgcolor: '#7869DF', color: '#fff' }} />

        <MarkdownRenderer content={entry.content} />
      </CardContent>
    </Card>
  );
};

export default ManualPage;
